import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, FormGroup, Label, Input, Alert, Card, CardBody, Table, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { BASE_URL, checkPermission, USER_PERMISSIONS } from '../../../constants';
import Select from 'react-select';

const AddInspectionPopup = ({open, setOpen, propertyId = null, date = null}) => {
	const navigate = useNavigate();
	const [errMsg, setErrMsg] = useState(null);
	const [properties, setProperties] = useState([]);
	const [selectedProperty, setSelectedProperty] = useState(propertyId || null);
	const [inspectionDate, setInspectionDate] = useState(date || '');
	const [inspectionTypes, setInspectionTypes] = useState([
		{ value: 'MOVE_IN', label: 'Move In' },
		{ value: 'MOVE_OUT', label: 'Move Out' },
		{ value: 'INTERMITTENT_INSPECTION', label: 'Intermittent' },
		{ value: 'MANAGER_INSPECTION', label: 'Manager' },
	]);
	const [inspectionType, setInspectionType] = useState(null);
	
	const [leaseTenants, setLeaseTenants] = useState([]);
	const [selectedTenants, setSelectedTenants] = useState([]);
	const userData = JSON.parse(localStorage.getItem("userData"));


	useEffect(() => {
		setErrMsg(null);
	}, [inspectionType]);

	useEffect(() => {
		const token = localStorage.getItem('token');
		if (token) {
			fetch(`${BASE_URL}/property/all`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})
				.then(response => response.json())
				.then(data => {
					if (data.status) {
						setProperties(data.result.map((property) => ({
							name: property.name,
							id: property.id
						})));
					}
					else {
						console.error("Error: Unable to fetch property data.");
					}
				})
				.catch(error => console.error("Error fetching data:", error));
		} else {
			console.error("Error: Authorization token is missing.");
		}
	}, []);

	useEffect(() => {
		if (selectedTenants && selectedTenants.length) {
			if (selectedTenants.find(t => t.value === 'create_renter_connection')) {
				navigate('/lease/add');
			}
		}
	}, [selectedTenants])

	useEffect(() => {
		setSelectedTenants([]);
		if (selectedProperty) {
			const token = localStorage.getItem('token');
			if (token) {
				fetch(`${BASE_URL}/propertylease/list/${selectedProperty}`, {
					headers: {
						Authorization: `Bearer ${token}`
					}
				})
					.then(response => response.json())
					.then(data => {
						if (data.status) {
							setLeaseTenants(data.result.data);
						}
						else {
							console.error("Error: Unable to fetch lease tenants data.");
						}
					})
					.catch(error => console.error("Error fetching data:", error));
			} else {
				console.error("Error: Authorization token is missing.");
			}
		}
	}, [selectedProperty]);

	const handleSubmit = () => {
		if (inspectionType == null) {
			setErrMsg('Please select an inspection type');
			return;
		}
		if (selectedProperty == null) {
			setErrMsg('Please select a property');
			return;
		}
		if (inspectionDate == '') {
			setErrMsg('Please select an inspection date');
			return;
		}

		if (!['INTERMITTENT_INSPECTION', 'MANAGER_INSPECTION'].includes(inspectionType) && selectedTenants.length === 0) {
			setErrMsg('Please select at least one renter');
			return;
		}
		
		setErrMsg(null);
		const token = localStorage.getItem('token');

		if (token && userData && userData.id) {

			const inspData = {
				property_id: selectedProperty,
				activity: inspectionType,
				tenant_id: selectedTenants.map(t => t.value),
				inspection_date: inspectionDate,
				inspected_by: userData.id,
			}
			console.log({inspData})

			axios.post(`${BASE_URL}/inspection/add`, inspData, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})
				.then(response => {
					const data = response.data;
					if (data.status) {
						setOpen(false);
					}
					else {
						setErrMsg(data.message);
					}
				})
				.catch(error => {
					console.error("Error:", error);
					setErrMsg(error.message);
				});
		}
	}

	if (!checkPermission(USER_PERMISSIONS.CREATE_INSPECTION)) {
		return null;
	}

	return (
		<Modal isOpen={open} centered size="md">
			<ModalHeader style={{ display: 'flex', justifyContent: 'space-between' }}
				close={
					<button className="close" onClick={() => setOpen(false)} style={{ outline: 'none', border: 'none', background: 'transparent' }}>
						<FaTimes size={20} />
					</button>
				}>
				Create Inspection
			</ModalHeader>
			<ModalBody>
				{errMsg && <Alert color="danger">{errMsg}</Alert>}

				{(inspectionType == null) ? (
					<>
						<FormGroup>
							<Label for="inspection_type">Inspection Type</Label>
							<div className='d-grid'>
								{inspectionTypes.map(type => (
									<Button color="primary" onClick={() => setInspectionType(type.value)}>{type.label}</Button>
								))}
							</div>
						</FormGroup>
					</>
				) : (
					<>
						<FormGroup>
							<Label for="property">Property</Label>
							<Input type="select" name="property" id="property" value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
								<option value="">Select Property</option>
								{properties.map(property => (
									<option value={property.id}>{property.name}</option>
								))}
							</Input>
						</FormGroup>

						{(!['INTERMITTENT_INSPECTION', 'MANAGER_INSPECTION'].includes(inspectionType)) ? (
						<FormGroup>
							<Label for="renters">Renters</Label>
							<Select
								name="tenant"
								id="tenant"
								isDisabled={selectedProperty == null}
								options={[
									...leaseTenants.map(t => ({ label: `${t.tenant_first_name} ${t.tenant_last_name}`, value: t.tenant_id })),
									{ label: 'Create Renter Connection', value: 'create_renter_connection' }
								]}
								value={selectedTenants}
								onChange={(option) => {
									setSelectedTenants(option);
								}}
								placeholder="Search and select renter"
								isMulti={true}
							/>
						</FormGroup>
						) : null}
						<FormGroup>
							<Label for="inspection_date">Inspection Date</Label>
							<Input type="date" name="inspection_date" id="inspection_date" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} />
						</FormGroup>
						<FormGroup>
							<Label for="inspection_name">Inspector Name</Label>
							<Input type="text" name="inspection_name" id="inspection_name" readOnly defaultValue={`${userData?.first_name} ${userData?.last_name}`} />
						</FormGroup>

						<h4>Instructions</h4>
						<p>Please fill in the below to document conditions at {inspectionTypes.find(i => i.value === inspectionType)?.label}.</p>
						<p>First tap the check mark above S mark all items as Satisfactory and then make adjustment as needed.</p>
						
						<div><strong>Move through each area marking items as:</strong></div>
						<div>N= New (just installed or painted)</div>
						<div>S= Satisfactory / Acceptable</div>
						<div>D= Existing Damage</div>
						<div>!= Needs attention</div>
						<div>N/A= Not Available</div>
						<br />
						<p>Hold selection may present additional options. Any item marked in red is required in order to submit the inspection.</p>
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={() => setOpen(false)}>Cancel</Button>
				<Button color="primary" onClick={handleSubmit}>Create Inspection</Button>
			</ModalFooter>
		</Modal>
	);
}

export default AddInspectionPopup;